import { convertToSlug } from '@/utils/slugify';

const headingVariants: { [key: string]: string } = {
  1: 'text-[36px] font-bold sm:text-[48px] sm:font-bold',
  2: 'text-[28px] font-bold sm:text-[36px] sm:font-bold',
  3: 'text-[22px] font-medium sm:text-[24px] sm:font-medium',
  4: 'text-[18px] font-medium sm:text-[20px] sm:font-medium',
  5: 'text-[16px] font-medium sm:text-[18px] sm:font-medium',
  6: 'text-[16px] font-normal sm:text-[18px] sm:font-normal',
};

// Convert heading level to variant class
const getHeadingVariant = (level: number): string => {
  return headingVariants[level.toString()] || 'headingOne'; // Fallback to headingOne if level is out of range
};

export const serializeToHTML = (nodes: any[]): string => {
  return nodes
    .map((node) => {
      switch (node.type) {
        case 'paragraph': {
          const content = serializeChildren(node.children);
          return content
            ? `<p class="text-base text-neutral-700 font-normal">${content}</p>`
            : ''; // Only render <p> if content is not empty
        }
        case 'heading': {
          const content = serializeChildren(node.children);
          const headingLevel = `h${node.level || 1}`; // Fallback to h1 if level is undefined
          const variantClass = getHeadingVariant(node.level || 1); // Get variant based on level

          const id = convertToSlug(content);

          return content
            ? `<${headingLevel} id="${id}" class="smzh-headline first:mt-0 mt-8 text-[#272835] ${variantClass}">${content}</${headingLevel}>`
            : '';
        }
        case 'list': {
          const listItems = serializeChildren(node.children);
          const isOrdered = node.format === 'ordered';
          const listTag = isOrdered ? 'ol' : 'ul';
          const listClass = isOrdered
            ? 'list-decimal pl-6 flex flex-col gap-3 marker:text-[#272835] text-[#272835]'
            : 'list-disc pl-4 flex flex-col gap-3 marker:text-neutral-600 text-[#272835] font-normal';
          return `<${listTag} class="${listClass}">${listItems}</${listTag}>`;
        }
        case 'list-item': {
          const content = serializeChildren(node.children);
          return `<li>${content}</li>`;
        }
        case 'quote': {
          const content = serializeChildren(node.children);
          return content
            ? `<blockquote class="text-brand-600 text-2xl font-black">${content}</blockquote>`
            : '';
        }
        default:
          return '';
      }
    })
    .join('');
};

// Helper function to serialize the children nodes
const serializeChildren = (children: any[]): string => {
  return children
    .map((child) => {
      if (child.type === 'text') {
        let textContent = child.text;

        // Apply formatting based on the properties in the text node
        if (child.bold) {
          textContent = `<strong>${textContent}</strong>`;
        }
        if (child.italic) {
          textContent = `<em>${textContent}</em>`;
        }
        if (child.underline) {
          textContent = `<u>${textContent}</u>`;
        }

        return textContent; // Return the formatted text content
      }
      if (child.type === 'link' && child.url) {
        const linkContent = serializeChildren(child.children || []);
        return `<a href="${child.url}" aria-label="${child.url}" class="underline underline-offset-2" target="_blank" rel="noopener noreferrer">${linkContent}</a>`;
      }
      // Recursively serialize nested children
      return serializeToHTML([child]);
    })
    .join('');
};
